// src/features/authn/OidcDiscoveryPanel.tsx
// Read-only OIDC discovery reference for admins wiring up third-party
// "Sign in with <platform>" integrations — GetOpenIDConfiguration + GetJWKS.
// Both are also public well-known endpoints; this is the authenticated
// admin's copy-paste-friendly view of the same data.

import { useState, useEffect, useCallback } from 'react';
import { observer } from '@react';
import { useAuthnOidc, useI18n } from '@react/hooks';
import { Button, Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter, ErrorAlert } from '@react/components';
import type { OpenIDConfiguration, JWKS } from '@core/types/authn';

function useCopyToClipboard() {
  const [copied, setCopied] = useState(false);
  const copy = useCallback(async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard unavailable
    }
  }, []);
  return { copied, copy };
}

export const OidcDiscoveryPanel = observer(() => {
  const oidc = useAuthnOidc();
  const { t } = useI18n();
  const { copied, copy } = useCopyToClipboard();

  const [config, setConfig] = useState<OpenIDConfiguration | null>(null);
  const [jwks, setJwks] = useState<JWKS | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    setLoading(true);
    Promise.all([oidc.GetOpenIDConfiguration(), oidc.GetJWKS()])
      .then(([c, j]) => {
        setConfig(c.configuration ?? null);
        setJwks(j.jwks ?? null);
      })
      .catch((err) => setError(err))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const endpoints = config
    ? [
        { label: t('authn.admin.issuer'), value: config.issuer },
        { label: t('authn.admin.authorizationEndpoint'), value: config.authorization_endpoint },
        { label: t('authn.admin.tokenEndpoint'), value: config.token_endpoint },
        { label: t('authn.admin.userinfoEndpoint'), value: config.userinfo_endpoint },
        { label: t('authn.admin.jwksUri'), value: config.jwks_uri },
        { label: t('authn.admin.revocationEndpoint'), value: config.revocation_endpoint },
        { label: t('authn.admin.introspectionEndpoint'), value: config.introspection_endpoint },
      ]
    : [];

  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => <div key={i} className="h-10 rounded-lg bg-muted animate-pulse" />)}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {!!error && <ErrorAlert error={error} dismissible onDismiss={() => setError(null)} />}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('authn.admin.discoveryTitle')}</CardTitle>
          <CardDescription>{t('authn.admin.discoveryDesc')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-1">
          {endpoints.map((e) => (
            <div key={e.label} className="flex items-center justify-between gap-2 py-2 border-b border-border last:border-0">
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground">{e.label}</p>
                <p className="text-sm font-mono text-foreground truncate">{e.value || '-'}</p>
              </div>
              {e.value && (
                <Button variant="ghost" size="sm" onClick={() => copy(e.value)} className="shrink-0">
                  {copied ? t('common.copied') : t('common.copy')}
                </Button>
              )}
            </div>
          ))}
        </CardContent>
        <CardFooter className="justify-end">
          <Button variant="secondary" size="sm" onClick={() => config && copy(JSON.stringify(config, null, 2))}>
            {t('authn.admin.copyFullDocument')}
          </Button>
        </CardFooter>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('authn.admin.jwksTitle')}</CardTitle>
          <CardDescription>{t('authn.admin.jwksDesc')}</CardDescription>
        </CardHeader>
        <CardContent>
          {(jwks?.keys?.items ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">{t('authn.admin.noKeys')}</p>
          ) : (
            <div className="space-y-2">
              {(jwks?.keys?.items ?? []).map((k) => (
                <div key={k.kid} className="flex items-center justify-between gap-2 p-3 rounded-lg border border-border">
                  <div className="min-w-0">
                    <p className="text-sm font-mono text-foreground truncate">{k.kid}</p>
                    <p className="text-xs text-muted-foreground">{k.kty} · {k.alg} · {k.use}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
        <CardFooter className="justify-end">
          <Button variant="secondary" size="sm" onClick={() => jwks && copy(JSON.stringify(jwks, null, 2))}>
            {t('authn.admin.copyFullDocument')}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
});

export default OidcDiscoveryPanel;
